import React, { useState } from "react";
import {
  LineChart, TrendingUp, Search, Sparkles, Briefcase, ShieldCheck,
  ArrowRight, Check, ChevronDown, Star, Activity, Menu, X,
} from "lucide-react";

const NAV_LINKS = [
  { id: "features", label: "Features" },
  { id: "how-it-works", label: "How it works" },
  { id: "pricing", label: "Pricing" },
  { id: "faq", label: "FAQ" },
];

const FEATURES = [
  {
    icon: LineChart,
    title: "Real-Time Technical Analysis",
    desc: "RSI, MACD, EMA(20/50/200) and Bollinger Bands computed live on every ticker you look at.",
  },
  {
    icon: Search,
    title: "Smart Swing Screener",
    desc: "Scans dozens of stocks against proven swing-trade criteria — momentum, trend, and volume, ranked in seconds.",
  },
  {
    icon: Sparkles,
    title: "AI Research Team",
    desc: "An AI research layer synthesizes every signal into a clear recommendation, with plain-English reasoning.",
  },
  {
    icon: Briefcase,
    title: "Portfolio & Wallet Tracking",
    desc: "Live P&L, trade history, and cash balance in one place — no spreadsheets required.",
  },
  {
    icon: TrendingUp,
    title: "Daily Movers",
    desc: "Top gainers and losers across your watchlist and curated stock lists, refreshed every session.",
  },
  {
    icon: ShieldCheck,
    title: "Built for Swing Traders",
    desc: "Confidence-rated recommendations with risk/reward scoring — trend, momentum, and volatility, weighed together.",
  },
];

const STEPS = [
  {
    n: "01",
    title: "Sign in with Google",
    desc: "Create your account in one click — no forms, no passwords to remember.",
  },
  {
    n: "02",
    title: "Screen & analyze",
    desc: "Run the swing screener, scan sectors, and let the AI research team surface the setups worth a look.",
  },
  {
    n: "03",
    title: "Track & grow",
    desc: "Log trades, watch your wallet balance update live, and refine your edge over time.",
  },
];

const TESTIMONIALS = [
  { initials: "JT", name: "J. Turner", role: "Swing trader, placeholder", quote: "Placeholder testimonial — this is where a real trader's quote about the screener and AI signals will go." },
  { initials: "AM", name: "A. Martinez", role: "Swing trader, placeholder", quote: "Placeholder testimonial — highlight how portfolio tracking and daily movers fit into a real workflow." },
  { initials: "RK", name: "R. Kapoor", role: "Swing trader, placeholder", quote: "Placeholder testimonial — swap in a quote about the AI research team once you have one." },
];

const PLANS = [
  { name: "Starter", price: "—", tagline: "Placeholder tier", features: ["Swing screener", "Watchlist & Daily Movers", "Core technical indicators"] },
  { name: "Pro", price: "—", tagline: "Placeholder tier", featured: true, features: ["Everything in Starter", "AI research team", "Portfolio & wallet tracking", "Priority support"] },
  { name: "Elite", price: "—", tagline: "Placeholder tier", features: ["Everything in Pro", "Extended stock universes", "Early access to new features"] },
];

const FAQS = [
  { q: "What markets does SwingTrader Pro cover?", a: "US equities — a curated screener universe plus sector-organized stock lists, with room to grow." },
  { q: "Do I need a brokerage account to use it?", a: "No. You can track a portfolio manually, or optionally connect a supported brokerage for live data." },
  { q: "Is there a mobile app?", a: "Placeholder answer — fill in once mobile support ships." },
  { q: "How is pricing structured?", a: "Placeholder answer — pricing details are coming soon." },
];

const btnPrimary = {
  background: "linear-gradient(135deg, var(--accent-blue), var(--accent-blue-dark))",
  color: "#fff",
  boxShadow: "0 8px 20px -6px rgba(67, 97, 238, 0.6)",
};

function NavBar({ onLogin, onGetStarted }) {
  const [mobileOpen, setMobileOpen] = useState(false);

  const scrollTo = (id) => {
    setMobileOpen(false);
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div
      className="sticky top-0 z-40"
      style={{ backgroundColor: "var(--bg-nav-backdrop)", backdropFilter: "blur(10px)", borderBottom: "1px solid var(--bg-secondary)" }}
    >
      <div className="max-w-7xl mx-auto px-5 md:px-8 h-16 flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <div
            className="w-9 h-9 rounded-xl flex items-center justify-center text-white font-bold text-sm shrink-0"
            style={{ background: "linear-gradient(135deg, var(--accent-blue), var(--accent-blue-dark))", boxShadow: "0 6px 18px -6px rgba(67, 97, 238, 0.7)" }}
          >
            ST
          </div>
          <div>
            <div className="font-bold text-sm leading-tight" style={{ color: "var(--text-primary)" }}>SwingTrader</div>
            <div className="text-[9px] font-semibold tracking-widest uppercase leading-tight" style={{ color: "#d9a63f" }}>Pro</div>
          </div>
        </div>

        <div className="hidden md:flex items-center gap-8">
          {NAV_LINKS.map((l) => (
            <button
              key={l.id}
              onClick={() => scrollTo(l.id)}
              className="text-sm font-medium transition-colors"
              style={{ color: "var(--text-secondary)" }}
              onMouseEnter={(e) => (e.currentTarget.style.color = "var(--text-primary)")}
              onMouseLeave={(e) => (e.currentTarget.style.color = "var(--text-secondary)")}
            >
              {l.label}
            </button>
          ))}
        </div>

        <div className="hidden md:flex items-center gap-3">
          <button
            onClick={onLogin}
            className="text-sm font-semibold px-4 py-2 rounded-lg transition-colors"
            style={{ color: "var(--text-secondary)" }}
            onMouseEnter={(e) => (e.currentTarget.style.color = "var(--text-primary)")}
            onMouseLeave={(e) => (e.currentTarget.style.color = "var(--text-secondary)")}
          >
            Log in
          </button>
          <button
            onClick={onGetStarted}
            className="text-sm font-semibold px-4 py-2.5 rounded-lg flex items-center gap-1.5 transition-transform hover:-translate-y-0.5"
            style={btnPrimary}
          >
            Get Started Free
            <ArrowRight size={15} />
          </button>
        </div>

        <button className="md:hidden" style={{ color: "var(--text-primary)" }} onClick={() => setMobileOpen((o) => !o)} aria-label="Menu">
          {mobileOpen ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {mobileOpen && (
        <div className="md:hidden px-5 pb-5 flex flex-col gap-1" style={{ borderTop: "1px solid var(--bg-secondary)" }}>
          {NAV_LINKS.map((l) => (
            <button
              key={l.id}
              onClick={() => scrollTo(l.id)}
              className="text-left text-sm font-medium py-2.5"
              style={{ color: "var(--text-secondary)" }}
            >
              {l.label}
            </button>
          ))}
          <div className="flex flex-col gap-2 mt-2">
            <button
              onClick={onLogin}
              className="text-sm font-semibold px-4 py-2.5 rounded-lg text-center"
              style={{ color: "var(--text-primary)", border: "1px solid var(--border-color)" }}
            >
              Log in
            </button>
            <button
              onClick={onGetStarted}
              className="text-sm font-semibold px-4 py-2.5 rounded-lg text-center flex items-center justify-center gap-1.5"
              style={btnPrimary}
            >
              Get Started Free
              <ArrowRight size={15} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function MockDashboardPreview() {
  const rows = [
    { t: "NVDA", p: "$949.83", c: "+4.23%", up: true },
    { t: "AAPL", p: "$231.06", c: "+1.12%", up: true },
    { t: "TSLA", p: "$402.71", c: "-0.84%", up: false },
  ];
  return (
    <div
      className="rounded-2xl p-4 sm:p-5 w-full max-w-md mx-auto relative"
      style={{ backgroundColor: "var(--bg-card)", border: "1px solid var(--border-color)", boxShadow: "0 1px 2px rgba(0,0,0,0.3), 0 28px 56px -20px rgba(0,0,0,0.75)" }}
    >
      <div
        className="absolute -top-3 right-4 text-[10px] font-semibold uppercase tracking-wide px-2.5 py-1 rounded-full"
        style={{ backgroundColor: "var(--bg-card)", border: "1px solid var(--border-color)", color: "var(--text-muted)" }}
      >
        Sample preview
      </div>

      <div className="flex items-center gap-1.5 mb-4">
        <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: "var(--loss-red)" }} />
        <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: "#eab308" }} />
        <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: "var(--gain-green)" }} />
      </div>

      <div className="grid grid-cols-3 gap-2 mb-4">
        {[
          { l: "S&P 500", v: "5,842", c: "+0.65%" },
          { l: "NASDAQ", v: "18,220", c: "+0.81%" },
          { l: "Score", v: "78.4", c: "Strong" },
        ].map((s) => (
          <div key={s.l} className="rounded-lg p-2.5" style={{ backgroundColor: "var(--bg-page)", border: "1px solid var(--border-color)" }}>
            <div className="text-[9px] uppercase tracking-wide mb-1" style={{ color: "var(--text-muted)" }}>{s.l}</div>
            <div className="text-xs font-bold" style={{ color: "var(--text-primary)" }}>{s.v}</div>
            <div className="text-[10px] font-semibold" style={{ color: "var(--text-green-soft)" }}>{s.c}</div>
          </div>
        ))}
      </div>

      <svg viewBox="0 0 300 80" className="w-full h-16 mb-4" preserveAspectRatio="none">
        <defs>
          <linearGradient id="landingChartFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="var(--accent-blue)" stopOpacity="0.35" />
            <stop offset="100%" stopColor="var(--accent-blue)" stopOpacity="0" />
          </linearGradient>
        </defs>
        <path
          d="M0,55 L25,48 L50,58 L75,40 L100,44 L125,28 L150,34 L175,20 L200,26 L225,14 L250,18 L275,6 L300,10 L300,80 L0,80 Z"
          fill="url(#landingChartFill)"
        />
        <path
          d="M0,55 L25,48 L50,58 L75,40 L100,44 L125,28 L150,34 L175,20 L200,26 L225,14 L250,18 L275,6 L300,10"
          fill="none"
          stroke="var(--link-blue)"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>

      <div className="space-y-1.5">
        {rows.map((r) => (
          <div key={r.t} className="flex items-center justify-between rounded-lg px-3 py-2" style={{ backgroundColor: "var(--bg-page)", border: "1px solid var(--bg-card)" }}>
            <span className="text-xs font-bold" style={{ color: "var(--text-primary)" }}>{r.t}</span>
            <span className="text-xs" style={{ color: "var(--text-secondary)" }}>{r.p}</span>
            <span className="text-xs font-semibold" style={{ color: r.up ? "var(--text-green-soft)" : "var(--text-red-soft)" }}>{r.c}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function Faq({ q, a }) {
  return (
    <details
      className="group rounded-xl px-5 py-4 transition-colors"
      style={{ backgroundColor: "var(--bg-card)", border: "1px solid var(--border-color)" }}
    >
      <summary className="flex items-center justify-between cursor-pointer list-none">
        <span className="text-sm font-semibold pr-4" style={{ color: "var(--text-primary)" }}>{q}</span>
        <ChevronDown size={16} className="shrink-0 transition-transform group-open:rotate-180" style={{ color: "var(--text-muted)" }} />
      </summary>
      <p className="text-sm mt-3 leading-relaxed" style={{ color: "var(--text-secondary)" }}>{a}</p>
    </details>
  );
}

export default function Landing({ onLogin, onGetStarted }) {
  return (
    <div style={{ backgroundColor: "var(--bg-page)", color: "var(--text-primary)", minHeight: "100vh" }} className="overflow-x-hidden">
      <NavBar onLogin={onLogin} onGetStarted={onGetStarted} />

      {/* Hero */}
      <section
        className="relative px-5 md:px-8 pt-16 pb-20 md:pt-24 md:pb-28"
        style={{
          background:
            "radial-gradient(ellipse 900px 600px at 15% 0%, rgba(67, 97, 238, 0.20), transparent 60%), radial-gradient(ellipse 700px 500px at 100% 10%, rgba(217, 166, 63, 0.10), transparent 55%)",
        }}
      >
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-14 items-center">
          <div className="animate-fade-in-up">
            <div
              className="inline-flex items-center gap-2 text-xs font-semibold px-3 py-1.5 rounded-full mb-6"
              style={{ backgroundColor: "rgba(67, 97, 238, 0.14)", border: "1px solid rgba(67, 97, 238, 0.3)", color: "var(--link-blue)" }}
            >
              <Activity size={13} />
              US Market · Real-time swing trading signals
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-[3.4rem] font-bold leading-[1.05] tracking-tight mb-5" style={{ color: "var(--text-primary)" }}>
              Trade with an edge,<br />not a guess.
            </h1>
            <p className="text-base sm:text-lg leading-relaxed mb-8 max-w-lg" style={{ color: "var(--text-secondary)" }}>
              Screeners, live technicals, fundamentals and an AI research team —
              built for swing traders who want signal, not noise.
            </p>
            <div className="flex flex-wrap items-center gap-4 mb-8">
              <button
                onClick={onGetStarted}
                className="text-sm font-semibold px-6 py-3 rounded-xl flex items-center gap-2 transition-transform hover:-translate-y-0.5"
                style={btnPrimary}
              >
                Get Started Free
                <ArrowRight size={16} />
              </button>
              <button
                onClick={() => document.getElementById("features")?.scrollIntoView({ behavior: "smooth" })}
                className="text-sm font-semibold px-6 py-3 rounded-xl transition-colors"
                style={{ color: "var(--text-primary)", border: "1px solid var(--border-color)", backgroundColor: "rgba(255,255,255,0.02)" }}
              >
                See features
              </button>
            </div>
            <div className="flex items-center gap-2 text-xs" style={{ color: "var(--text-muted)" }}>
              <Check size={14} style={{ color: "var(--text-green-soft)" }} />
              Sign in with Google in seconds
            </div>
          </div>

          <div className="animate-fade-in-up">
            <MockDashboardPreview />
          </div>
        </div>
      </section>

      {/* Stats strip */}
      <section className="px-5 md:px-8 py-10" style={{ borderTop: "1px solid var(--bg-secondary)", borderBottom: "1px solid var(--bg-secondary)", backgroundColor: "#0d0e16" }}>
        <div className="max-w-7xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
          {[
            { v: "100+", l: "Stocks tracked" },
            { v: "8", l: "Sectors covered" },
            { v: "RSI · MACD · EMA · BB", l: "Live indicators" },
            { v: "AI-Powered", l: "Signal synthesis" },
          ].map((s) => (
            <div key={s.l}>
              <div className="text-lg sm:text-xl font-bold mb-1" style={{ color: "var(--text-primary)" }}>{s.v}</div>
              <div className="text-xs" style={{ color: "var(--text-muted)" }}>{s.l}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Features */}
      <section id="features" className="px-5 md:px-8 py-20 md:py-28 max-w-7xl mx-auto">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <div className="text-xs font-semibold uppercase tracking-widest mb-3" style={{ color: "#d9a63f" }}>Features</div>
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mb-4" style={{ color: "var(--text-primary)" }}>
            Everything you need to swing trade with confidence
          </h2>
          <p className="text-base" style={{ color: "var(--text-secondary)" }}>
            One platform for screening, analysis, and portfolio tracking — no more juggling five tabs.
          </p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {FEATURES.map(({ icon: Icon, title, desc }) => (
            <div
              key={title}
              className="rounded-2xl p-6 hover-lift transition-colors"
              style={{ backgroundColor: "var(--bg-card)", border: "1px solid var(--border-color)" }}
            >
              <div
                className="w-11 h-11 rounded-xl flex items-center justify-center mb-4"
                style={{ backgroundColor: "rgba(67, 97, 238, 0.14)", border: "1px solid rgba(67, 97, 238, 0.3)" }}
              >
                <Icon size={19} style={{ color: "var(--link-blue)" }} />
              </div>
              <h3 className="text-base font-bold mb-2" style={{ color: "var(--text-primary)" }}>{title}</h3>
              <p className="text-sm leading-relaxed" style={{ color: "var(--text-secondary)" }}>{desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section id="how-it-works" className="px-5 md:px-8 py-20 md:py-28" style={{ backgroundColor: "#0d0e16", borderTop: "1px solid var(--bg-secondary)", borderBottom: "1px solid var(--bg-secondary)" }}>
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <div className="text-xs font-semibold uppercase tracking-widest mb-3" style={{ color: "#d9a63f" }}>How it works</div>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight" style={{ color: "var(--text-primary)" }}>
              From sign-in to your first trade in minutes
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {STEPS.map((s) => (
              <div key={s.n} className="relative">
                <div className="text-4xl font-bold mb-4" style={{ color: "var(--bg-hover)" }}>{s.n}</div>
                <h3 className="text-lg font-bold mb-2" style={{ color: "var(--text-primary)" }}>{s.title}</h3>
                <p className="text-sm leading-relaxed" style={{ color: "var(--text-secondary)" }}>{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials (placeholder) */}
      <section className="px-5 md:px-8 py-20 md:py-28 max-w-7xl mx-auto">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <div className="text-xs font-semibold uppercase tracking-widest mb-3" style={{ color: "#d9a63f" }}>Testimonials</div>
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mb-3" style={{ color: "var(--text-primary)" }}>
            What traders are saying
          </h2>
          <p className="text-sm" style={{ color: "var(--text-muted)" }}>Placeholder quotes — swap these in once you have real feedback.</p>
        </div>
        <div className="grid md:grid-cols-3 gap-5">
          {TESTIMONIALS.map((t) => (
            <div key={t.name} className="rounded-2xl p-6" style={{ backgroundColor: "var(--bg-card)", border: "1px solid var(--border-color)" }}>
              <div className="flex items-center gap-1 mb-3">
                {[...Array(5)].map((_, i) => <Star key={i} size={13} fill="#d9a63f" style={{ color: "#d9a63f" }} />)}
              </div>
              <p className="text-sm leading-relaxed mb-5" style={{ color: "var(--text-secondary)" }}>&ldquo;{t.quote}&rdquo;</p>
              <div className="flex items-center gap-3">
                <div
                  className="w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold text-white shrink-0"
                  style={{ backgroundColor: "var(--accent-blue)" }}
                >
                  {t.initials}
                </div>
                <div>
                  <div className="text-sm font-semibold" style={{ color: "var(--text-primary)" }}>{t.name}</div>
                  <div className="text-xs" style={{ color: "var(--text-muted)" }}>{t.role}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Pricing (placeholder) */}
      <section id="pricing" className="px-5 md:px-8 py-20 md:py-28" style={{ backgroundColor: "#0d0e16", borderTop: "1px solid var(--bg-secondary)", borderBottom: "1px solid var(--bg-secondary)" }}>
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <div className="text-xs font-semibold uppercase tracking-widest mb-3" style={{ color: "#d9a63f" }}>Pricing</div>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mb-3" style={{ color: "var(--text-primary)" }}>
              Simple plans for every trader
            </h2>
            <p className="text-sm" style={{ color: "var(--text-muted)" }}>Placeholder tiers — pricing coming soon.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-6 items-start">
            {PLANS.map((p) => (
              <div
                key={p.name}
                className="rounded-2xl p-7 relative"
                style={{
                  backgroundColor: p.featured ? "var(--bg-hover)" : "var(--bg-card)",
                  border: p.featured ? "1px solid var(--accent-blue)" : "1px solid var(--border-color)",
                  boxShadow: p.featured ? "0 0 0 1px rgba(67, 97, 238, 0.4), 0 20px 40px -16px rgba(67, 97, 238, 0.35)" : "none",
                }}
              >
                {p.featured && (
                  <div
                    className="absolute -top-3 left-1/2 -translate-x-1/2 text-[10px] font-bold uppercase tracking-wide px-3 py-1 rounded-full"
                    style={{ background: "linear-gradient(135deg, var(--accent-blue), var(--accent-blue-dark))", color: "#fff" }}
                  >
                    Most popular
                  </div>
                )}
                <div className="text-sm font-semibold mb-1" style={{ color: "var(--text-secondary)" }}>{p.name}</div>
                <div className="text-3xl font-bold mb-1" style={{ color: "var(--text-primary)" }}>{p.price}<span className="text-sm font-medium" style={{ color: "var(--text-muted)" }}>/mo</span></div>
                <div className="text-xs mb-6" style={{ color: "var(--text-muted)" }}>{p.tagline}</div>
                <ul className="space-y-2.5 mb-7">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm" style={{ color: "var(--text-secondary)" }}>
                      <Check size={15} className="mt-0.5 shrink-0" style={{ color: "var(--text-green-soft)" }} />
                      {f}
                    </li>
                  ))}
                </ul>
                <button
                  onClick={onGetStarted}
                  className="w-full text-sm font-semibold py-2.5 rounded-lg transition-colors"
                  style={p.featured ? btnPrimary : { color: "var(--text-primary)", border: "1px solid var(--border-color)" }}
                >
                  Get started
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="px-5 md:px-8 py-20 md:py-28 max-w-3xl mx-auto">
        <div className="text-center mb-12">
          <div className="text-xs font-semibold uppercase tracking-widest mb-3" style={{ color: "#d9a63f" }}>FAQ</div>
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight" style={{ color: "var(--text-primary)" }}>
            Frequently asked questions
          </h2>
        </div>
        <div className="space-y-3">
          {FAQS.map((f) => <Faq key={f.q} {...f} />)}
        </div>
      </section>

      {/* Final CTA */}
      <section className="px-5 md:px-8 py-20 md:py-24">
        <div
          className="max-w-5xl mx-auto rounded-3xl px-8 py-14 md:py-16 text-center relative overflow-hidden"
          style={{
            background: "linear-gradient(135deg, #1b2145, #14172a)",
            border: "1px solid var(--border-color)",
          }}
        >
          <div
            className="absolute inset-0 opacity-60"
            style={{ background: "radial-gradient(ellipse 600px 400px at 50% 0%, rgba(67, 97, 238, 0.25), transparent 60%)" }}
          />
          <div className="relative">
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mb-4" style={{ color: "var(--text-primary)" }}>
              Ready to trade with an edge?
            </h2>
            <p className="text-base mb-8 max-w-lg mx-auto" style={{ color: "var(--text-secondary)" }}>
              Sign in with Google and get straight to your dashboard — screeners, signals, and AI research, all in one place.
            </p>
            <button
              onClick={onGetStarted}
              className="text-sm font-semibold px-7 py-3.5 rounded-xl inline-flex items-center gap-2 transition-transform hover:-translate-y-0.5"
              style={btnPrimary}
            >
              Get Started Free
              <ArrowRight size={16} />
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="px-5 md:px-8 pt-14 pb-8" style={{ borderTop: "1px solid var(--bg-secondary)" }}>
        <div className="max-w-7xl mx-auto">
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-10 mb-12">
            <div className="lg:col-span-2">
              <div className="flex items-center gap-2.5 mb-4">
                <div
                  className="w-8 h-8 rounded-lg flex items-center justify-center text-white font-bold text-xs"
                  style={{ background: "linear-gradient(135deg, var(--accent-blue), var(--accent-blue-dark))" }}
                >
                  ST
                </div>
                <div className="font-bold text-sm" style={{ color: "var(--text-primary)" }}>SwingTrader Pro</div>
              </div>
              <p className="text-sm max-w-xs leading-relaxed" style={{ color: "var(--text-muted)" }}>
                Swing trading signals &amp; analytics for the US stock market.
              </p>
            </div>
            {[
              { h: "Product", links: ["Features", "Pricing", "FAQ"] },
              { h: "Company", links: ["About", "Blog", "Careers"] },
              { h: "Legal", links: ["Privacy Policy", "Terms of Service"] },
            ].map((col) => (
              <div key={col.h}>
                <div className="text-xs font-semibold uppercase tracking-wide mb-4" style={{ color: "var(--text-secondary)" }}>{col.h}</div>
                <ul className="space-y-2.5">
                  {col.links.map((l) => (
                    <li key={l}>
                      <button type="button" className="text-sm text-left transition-colors" style={{ color: "var(--text-muted)" }}>{l}</button>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <div
            className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-6 text-xs"
            style={{ borderTop: "1px solid var(--bg-secondary)", color: "#4a5170" }}
          >
            <span>© {new Date().getFullYear()} SwingTrader Pro. All rights reserved.</span>
            <span>US Market · Swing trading signals &amp; analytics</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
